// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/components/ProviderSubmissionCard.tsx

import type {
  CommunityNavigatorNextActionCode,
  NavigatorCaseDetail,
} from "../types";
import { formatDateTime } from "../intakeUtils";
import DetailItem from "./DetailItem";

type ProviderSubmissionCardProps = {
  caseDetail: NavigatorCaseDetail;
  nextActionCode: CommunityNavigatorNextActionCode | null;
  nextActionLabel: string;
  canCloseCase: boolean;
  canRunMatching?: boolean;
  showNoEmailPhoneHint?: boolean;
  onOpenMatching: () => void;
  onOpenPatient: () => void;
  onCloseCase: () => void;
  onViewProviderProfile?: (familyDoctorId: number) => void;
};

type ProviderRequestLike = NonNullable<NavigatorCaseDetail["providerRequest"]>;

function getLatestProviderRequest(caseDetail: NavigatorCaseDetail) {
  const history = Array.isArray(caseDetail.providerRequestHistory)
    ? caseDetail.providerRequestHistory
    : [];

  return history[0] || caseDetail.providerRequest || null;
}

function getFollowUpAction(
  code: CommunityNavigatorNextActionCode | null,
  hasProviderRequest: boolean,
  canCloseCase: boolean,
  canRunMatching: boolean,
  onOpenMatching: () => void,
  onOpenPatient: () => void,
  onCloseCase: () => void
): { label: string; onClick: () => void } | null {
  if (
    code === "Closed" ||
    code === "WaitingForClinic" ||
    code === "MeetAndGreetInProgress" ||
    code === "ContactPatientForMeetAndGreet"
  ) {
    return null;
  }

  if (
    (code === "MonitorWaitlist" || code === "ContactPatientAboutWaitlist") &&
    canRunMatching
  ) {
    return { label: "Find another provider", onClick: onOpenMatching };
  }

  if (code === "FindAnotherMatch") {
    return { label: "Find another match", onClick: onOpenMatching };
  }

  if (code === "SelectProvider") {
    return { label: "Review matches", onClick: onOpenMatching };
  }

  if (code === "NoSuitableMatch" && hasProviderRequest) {
    return { label: "Review matches", onClick: onOpenMatching };
  }

  if (code === "FollowUpWithPatient") {
    return { label: "Open patient & contact", onClick: onOpenPatient };
  }

  if (code === "CloseCase" && canCloseCase) {
    return { label: "Close case", onClick: onCloseCase };
  }

  return null;
}

function requestStatusLabel(request: ProviderRequestLike | null) {
  const label = String(request?.statusLabel || "").trim();
  return label || "—";
}

function requestTime(request: ProviderRequestLike | null) {
  return request?.forwardedAt || request?.submittedAt || null;
}

export default function ProviderSubmissionCard({
  caseDetail,
  nextActionCode,
  nextActionLabel,
  canCloseCase,
  canRunMatching = false,
  showNoEmailPhoneHint = false,
  onOpenMatching,
  onOpenPatient,
  onCloseCase,
  onViewProviderProfile,
}: ProviderSubmissionCardProps) {
  const providerRequest = getLatestProviderRequest(caseDetail);
  const followUpAction = getFollowUpAction(
    nextActionCode,
    Boolean(providerRequest),
    canCloseCase,
    canRunMatching,
    onOpenMatching,
    onOpenPatient,
    onCloseCase
  );
  const providerProfileId = Number(providerRequest?.familyDoctorProfileId || 0);
  const canViewProviderProfile =
    typeof onViewProviderProfile === "function" &&
    Number.isFinite(providerProfileId) &&
    providerProfileId > 0;
  const showWaitlistRematchNote =
    (nextActionCode === "MonitorWaitlist" ||
      nextActionCode === "ContactPatientAboutWaitlist") &&
    Boolean(followUpAction);

  return (
    <section className="cn-case-detail-card cn-case-detail-wide cn-follow-up-outcome">
      <div className="cn-case-detail-card-header">
        <h2>Current outcome</h2>
      </div>

      <dl className="cn-case-detail-list cn-case-detail-list-three">
        <DetailItem label="Next action" value={nextActionLabel || "—"} />
        <DetailItem
          label="Provider"
          value={
            providerRequest?.familyDoctorName ||
            caseDetail.selectedFamilyDoctorName ||
            "—"
          }
        />
        <DetailItem
          label="Clinic"
          value={
            providerRequest?.clinicName ||
            caseDetail.selectedClinicName ||
            "—"
          }
        />
        <DetailItem
          label="Request status"
          value={requestStatusLabel(providerRequest)}
        />
        <DetailItem
          label="Forwarded"
          value={formatDateTime(requestTime(providerRequest))}
        />
      </dl>

      {showWaitlistRematchNote ? (
        <p className="cn-waitlist-rematch-note">
          The patient can remain on this waitlist while you look for another
          provider.
        </p>
      ) : null}

      {showNoEmailPhoneHint ? (
        <p className="cn-no-email-phone-hint">
          No email — contact patient by phone
        </p>
      ) : null}

      {followUpAction || canViewProviderProfile ? (
        <div className="cn-follow-up-outcome-action">
          {canViewProviderProfile ? (
            <button
              type="button"
              className="cn-follow-up-outcome-secondary"
              onClick={() => onViewProviderProfile?.(providerProfileId)}
            >
              View provider profile
            </button>
          ) : null}
          {followUpAction ? (
            <button type="button" onClick={followUpAction.onClick}>
              {followUpAction.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
